import { Avatar, Box, Stack, Typography, Skeleton } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocales } from 'src/locales';
import countriesJson from 'src/utils/countries.json';
import Iconify from 'src/components/iconify';
import CiCard from './CiCard';
import CustomTooltip from './CustomTooltip';
import HoverShortcut from './HoverShortcut';
import { domainFavicon } from '../utils';

export interface TopCompetitorStat {
  domain: string;
  paidPresenceRate: number;
}

interface TopCompetitorsCardProps {
  title: string;
  dateRange: string;
  mainContent?: React.ReactNode;
  competitors?: TopCompetitorStat[];
  loading?: boolean;
  selectedCountry?: string | null;
  /** When true, render only header + list (non-brand structure) for use inside non-brand card wrapper + footer */
  contentOnly?: boolean;
  /** Right-side header label when contentOnly (e.g. "Paid presence rate") */
  rightLabel?: string;
}

const DEFAULT_COMPETITORS: TopCompetitorStat[] = [
];

// Height the card wrapper leaves between its header and its 30px footer. Fixed so
// the three cards of the bar stay the same height whatever they have to show.
const CONTENT_HEIGHT = 127;

function BrandFavicon({ brandDomain, size, highlight }: { brandDomain: string; size: number; highlight?: boolean }) {
  useEffect(() => { }, [brandDomain]);
  if (!brandDomain) return null;
  const faviconUrl = domainFavicon(brandDomain);
  return (
    <Avatar
      src={faviconUrl}
      alt={brandDomain}
      sx={{
        width: size,
        height: size,
        bgcolor: highlight ? '#20D873' : 'background.paper',
        color: highlight ? 'common.white' : 'text.primary',
        border: highlight ? 'none' : '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
      }}
    />
  );
}

// Figma: the share bar and the competitor row share the same 26px height.
const ROW_HEIGHT = 26;

function TopCompetitorBar({
  value,
  domain,
  index,
  selectedCountry,
}: {
  value: number;
  domain: string;
  index: number;
  selectedCountry?: string | null;
}) {
  const navigate = useNavigate();
  const { t } = useLocales();
  const [hovered, setHovered] = useState(false);
  const cleanDomain = domain
    ?.replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '')
    .trim() || domain;

  const handleShortcutClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const countryEntry = countriesJson?.find((c) => c.name === selectedCountry);
    const code = countryEntry?.code?.toUpperCase() || '';
    navigate(`/brand/visibility/ads?country=${encodeURIComponent(code)}&domain=${encodeURIComponent(domain)}`);
  };

  const hoverLabel = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {t('dashboard.global.visibility.seeYourAds')}
      <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
    </Box>
  );

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'relative',
        width: '100%',
        borderRadius: 1,
        overflow: 'hidden',
        height: ROW_HEIGHT,
      }}
    >
      {/* Progress bar background (same as non-brand) */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: '100%',
          width: `${value}%`,
          bgcolor: '#E2E5E940',
          borderRadius: 1,
          transition: 'width 0.3s ease',
        }}
      />
      {/* Content: percentage + favicon + domain (same order and style as non-brand) */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          px: 1,
        }}
      >
        <Typography
          sx={{
            color: '#333C47',
            fontFamily: 'Public Sans, sans-serif',
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '100%',
            fontVariantNumeric: 'tabular-nums',
            minWidth: 36,
            textAlign: 'right',
          }}
        >
          {value === 0 ? '<1%' : `${value}%`}
        </Typography>
        <BrandFavicon brandDomain={domain} size={18} highlight={index === 2} />
        <Typography
          sx={{
            color: '#333C47',
            fontFamily: 'Public Sans, sans-serif',
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '100%',
          }}
        >
          {cleanDomain}
        </Typography>
      </Stack>
      {selectedCountry && hovered && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            pl: 3,
            pr: 0.5,
            background: 'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(237,238,240,0.95) 30%)',
            backdropFilter: 'blur(2px)',
            borderRadius: 1,
            zIndex: 2,
          }}
        >
          <HoverShortcut
            defaultContent=""
            hoverContent={hoverLabel}
            onClick={handleShortcutClick}
            forceHovered
            fontSize={13}
            sx={{ ml: 0 }}
          />
        </Box>
      )}
    </Box>
  );
}

export default function TopCompetitorsCard({ competitors, loading, selectedCountry, contentOnly, rightLabel, ...props }: TopCompetitorsCardProps) {
  const { t } = useLocales();
  const displayCompetitors = (competitors && competitors.length > 0 ? competitors : DEFAULT_COMPETITORS).slice(0, 3);
  const headerRight = rightLabel ?? t('dashboard.visibility.paidPresenceRate');
  const topCompetitorsTooltip = t('dashboard.global.visibility.tooltips.topCompetitors');

  const mainContent = displayCompetitors.length > 0 ? (
    <Stack direction="column" spacing={1.5} sx={{ py: 1, width: '100%' }}>
      {displayCompetitors.map((competitor, index) => (
        <Stack direction="column" spacing={1.5} key={competitor.domain}>
          <TopCompetitorBar
            value={Math.round(competitor.paidPresenceRate)}
            domain={competitor.domain}
            index={index}
            selectedCountry={selectedCountry}
          />
        </Stack>
      ))}
    </Stack>
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '120px',
        marginY: '30px',
        color: 'text.secondary',
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
        {t('dashboard.global.no_data_available')}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '12px', mt: 1 }}>
        {t('dashboard.global.visibility.no_results_found_description')}
      </Typography>
    </Box>
  );

  if (contentOnly) {
    return (
      <>
        <Stack
          className="card-top-header"
          borderBottom={1}
          borderColor="divider"
          direction="row"
          py={1}
          px={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Typography
              className="card-top-header-item-label"
              variant="h6"
              color="text.primary"
              component="span"
            >
              {props.title}
            </Typography>

            <CustomTooltip title={topCompetitorsTooltip} placement="top">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                    stroke="#637381"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
            </CustomTooltip>
          </Box>

          <Typography
            className="card-top-header-item-value"
            variant="h6"
            color="text.primary"
          >
            {headerRight}
          </Typography>
        </Stack>
        <Stack
          width="100%"
          height={CONTENT_HEIGHT}
          minHeight={CONTENT_HEIGHT}
          maxHeight={CONTENT_HEIGHT}
          sx={{
            px: 0,
            py: 0,
          }}
        >
          {loading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
            />
          ) : displayCompetitors.length === 0 ? (
            <Stack height="100%" justifyContent="center" alignItems="center">
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mt: 2 }}
              >
                {t('dashboard.global.no_data_available')}
              </Typography>
            </Stack>
          ) : (
            <Stack
              direction="column"
              spacing={0.5}
              sx={{
                px: 2,
                py: '12px',
                boxSizing: 'border-box',
              }}
            >
              {displayCompetitors.map((competitor, index) => (
                <TopCompetitorBar
                  key={competitor.domain}
                  value={Math.round(competitor.paidPresenceRate)}
                  domain={competitor.domain}
                  index={index}
                  selectedCountry={selectedCountry}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </>
    );
  }

  return (
    <CiCard
      {...props}
      titleTooltip={topCompetitorsTooltip}
      mainContent={mainContent}
      loading={loading}
      topMetric={headerRight}
    />
  );
}
